import { Link } from 'react-router-dom';
import type {
  KidsAgeGroup,
  KidsAvatar,
  KidsLearningPath,
  KidsMediaRelation,
} from './types';

type CardGridItem = {
  title: string;
  text: string;
  imageUrl?: string;
  imageAlt?: string;
};

export function CardGrid({ items }: { items: Array<string[] | CardGridItem> }) {
  return (
    <div className="card-grid">
      {items.map((item) => {
        const card = Array.isArray(item) ? { title: item[0], text: item[1] } : item;
        return (
          <article className="tactile-card" key={card.title}>
            {card.imageUrl ? (
              <img className="card-media" src={card.imageUrl} alt={card.imageAlt ?? card.title} />
            ) : (
              <span className="card-icon">{card.title.slice(0, 2)}</span>
            )}
            <h3>{card.title}</h3>
            <p>{card.text}</p>
          </article>
        );
      })}
    </div>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <Link to="/kids/about">About</Link>
      <Link to="/kids/parent-guide">Parent Guide</Link>
      <Link to="/kids/faq">FAQ</Link>
      <Link to="/kids/login">Parent Login</Link>
      <span>Privacy and Terms</span>
    </footer>
  );
}

export function LoginPageView({
  children,
}: {
  children: React.ReactNode;
}) {
  return <>{children}</>;
}

type AuthCardProps = {
  mode: 'login' | 'signup';
  form: {
    errors: Record<string, string | undefined>;
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleGoogleSignIn: () => void;
    handleMagicLink: () => void;
    handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
    status: 'idle' | 'submitting' | 'success' | 'magic-link' | 'fallback';
    values: {
      parentName: string;
      email: string;
      password: string;
    };
  };
};

export function AuthCardView({ mode, form }: AuthCardProps) {
  return (
    <section className="auth-layout">
      <form className="auth-card" onSubmit={form.handleSubmit} noValidate>
        <p className="eyebrow">{mode === 'login' ? 'Parent Login' : 'Create Parent Account'}</p>
        <h1>{mode === 'login' ? 'Welcome back to your family dashboard.' : 'Start your child’s Quran journey.'}</h1>
        <p>
          {mode === 'login'
            ? 'Use your parent account to manage profiles, progress, settings, and learning paths.'
            : 'Create the parent account first. Then you can add child profiles and choose learning paths.'}
        </p>
        <button className="button button-secondary full" disabled={form.status === 'submitting'} onClick={form.handleGoogleSignIn} type="button">
          {form.status === 'submitting' ? 'Opening Google...' : 'Continue with Google'}
        </button>
        <div className="auth-divider" role="separator">
          <span>or use email</span>
        </div>
        {mode === 'signup' ? (
          <label>
            Parent name
            <input name="parentName" onChange={form.handleChange} placeholder="Your name" value={form.values.parentName} />
            {form.errors.parentName ? <span className="field-error">{form.errors.parentName}</span> : null}
          </label>
        ) : null}
        <label>
          Email
          <input name="email" onChange={form.handleChange} placeholder="parent@example.com" type="email" value={form.values.email} />
          {form.errors.email ? <span className="field-error">{form.errors.email}</span> : null}
        </label>
        <label>
          Password
          <input name="password" onChange={form.handleChange} placeholder="Password" type="password" value={form.values.password} />
          {form.errors.password ? <span className="field-error">{form.errors.password}</span> : null}
        </label>
        <button className="button button-primary full" disabled={form.status === 'submitting'} type="submit">
          {form.status === 'submitting' ? 'Working...' : mode === 'login' ? 'Continue' : 'Create Account'}
        </button>
        <button className="button button-secondary full" disabled={!form.values.email || form.status === 'submitting'} onClick={form.handleMagicLink} type="button">
          Send Magic Link
        </button>
        {form.status === 'success' ? (
          <p className="form-success">{mode === 'login' ? 'Signed in. Continue to profile setup.' : 'Account created. Check email if verification is required.'}</p>
        ) : null}
        {form.status === 'magic-link' ? <p className="form-success">Magic link requested. Check your email.</p> : null}
        {form.status === 'fallback' ? (
          <p className="form-note">We could not complete sign-in right now. Please check your details and try again.</p>
        ) : null}
        <p className="form-note">
          {mode === 'login'
            ? 'After sign-in, we will take you to profile setup automatically.'
            : 'After account creation, we will take you to profile setup automatically.'}
        </p>
        <Link className="ghost-link" to={mode === 'login' ? '/kids/signup' : '/kids/login'}>
          {mode === 'login' ? 'Create an account' : 'Already have an account?'}
        </Link>
        <Link className="ghost-link" to="/kids/request-parent">
          Child with Gmail? Ask a parent to add you
        </Link>
      </form>
    </section>
  );
}

type ChildAccessRequestViewProps = {
  form: {
    errors: Record<string, string | undefined>;
    handleChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
    status: 'idle' | 'submitting' | 'success' | 'error';
    values: {
      childName: string;
      childEmail: string;
      parentEmail: string;
      ageGroupSlug: string;
      message: string;
    };
  };
};

export function ChildAccessRequestPageView({ form }: ChildAccessRequestViewProps) {
  return (
    <section className="auth-layout">
      <form className="auth-card" onSubmit={form.handleSubmit} noValidate>
        <p className="eyebrow">Child Request</p>
        <h1>Ask your parent to add you.</h1>
        <p>Use your Gmail and your parent’s email. Your parent approves the request before your profile is created.</p>
        <label>
          Your name
          <input name="childName" onChange={form.handleChange} placeholder="Maryam" value={form.values.childName} />
          {form.errors.childName ? <span className="field-error">{form.errors.childName}</span> : null}
        </label>
        <label>
          Your Gmail
          <input name="childEmail" onChange={form.handleChange} placeholder="child@gmail.com" type="email" value={form.values.childEmail} />
          {form.errors.childEmail ? <span className="field-error">{form.errors.childEmail}</span> : null}
        </label>
        <label>
          Parent email
          <input name="parentEmail" onChange={form.handleChange} placeholder="parent@example.com" type="email" value={form.values.parentEmail} />
          {form.errors.parentEmail ? <span className="field-error">{form.errors.parentEmail}</span> : null}
        </label>
        <label>
          Age group
          <select name="ageGroupSlug" onChange={form.handleChange} value={form.values.ageGroupSlug}>
            <option value="ages-5-6">5-6 years</option>
            <option value="ages-7-9">7-9 years</option>
            <option value="ages-10-12">10-12 years</option>
          </select>
        </label>
        <label>
          Message for parent
          <textarea name="message" onChange={form.handleChange} placeholder="I want to learn Quran quizzes on Alhudah Kids." value={form.values.message} />
          {form.errors.message ? <span className="field-error">{form.errors.message}</span> : null}
        </label>
        <button className="button button-primary full" disabled={form.status === 'submitting'} type="submit">
          {form.status === 'submitting' ? 'Sending...' : 'Send Parent Request'}
        </button>
        {form.status === 'success' ? <p className="form-success">Request sent. Your parent can approve it from Manage Children.</p> : null}
        {form.status === 'error' ? <p className="form-note">We could not send this request. Check the emails and try again.</p> : null}
        <Link className="ghost-link" to="/kids/login">
          Back to Parent Login
        </Link>
      </form>
    </section>
  );
}

type ProfileSetupViewProps = {
  form: {
    ageGroups: KidsAgeGroup[];
    avatars: KidsAvatar[];
    errors: Record<string, string | undefined>;
    handleChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
    status: 'idle' | 'submitting' | 'success' | 'fallback';
    values: {
      displayName: string;
      ageGroup: string;
      avatarKey: string;
      language: string;
      dailyGoal: string;
    };
  };
  fallbackAgeGroups: KidsAgeGroup[];
  fallbackAvatars: KidsAvatar[];
  mediaImageUrl: (media: KidsMediaRelation | undefined) => string | undefined;
  mediaAltText: (media: KidsMediaRelation | undefined, fallback: string) => string;
};

export function ProfileSetupPageView({
  form,
  fallbackAgeGroups,
  fallbackAvatars,
  mediaImageUrl,
  mediaAltText,
}: ProfileSetupViewProps) {
  const ageGroups = form.ageGroups.length ? form.ageGroups : fallbackAgeGroups;
  const avatars = form.avatars.length ? form.avatars : fallbackAvatars;

  return (
    <section className="auth-layout">
      <form className="auth-card wide" onSubmit={form.handleSubmit} noValidate>
        <p className="eyebrow">Child Profile</p>
        <h1>Personalize the first learning profile.</h1>
        <div className="form-grid">
          <label>
            Child name
            <input name="displayName" onChange={form.handleChange} placeholder="Maryam" value={form.values.displayName} />
            {form.errors.displayName ? <span className="field-error">{form.errors.displayName}</span> : null}
          </label>
          <label>
            Age group
            <select name="ageGroup" onChange={form.handleChange} value={form.values.ageGroup}>
              {ageGroups.map((ageGroup) => (
                <option key={ageGroup.slug} value={ageGroup.slug}>
                  {ageGroup.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Learning language
            <select name="language" onChange={form.handleChange} value={form.values.language}>
              <option value="en">English</option>
              <option value="ar">Arabic</option>
              <option value="ur">Urdu</option>
            </select>
          </label>
          <label>
            Daily goal
            <select name="dailyGoal" onChange={form.handleChange} value={form.values.dailyGoal}>
              <option value="5">5 minutes</option>
              <option value="10">10 minutes</option>
              <option value="15">15 minutes</option>
            </select>
          </label>
        </div>
        <fieldset className="choice-group">
          <legend>Choose avatar</legend>
          {avatars.map((avatar) => (
            <label className="choice-pill avatar-choice" key={avatar.slug}>
              <input
                checked={form.values.avatarKey === avatar.slug}
                name="avatarKey"
                onChange={form.handleChange}
                type="radio"
                value={avatar.slug}
              />
              {mediaImageUrl(avatar.media) ? (
                <img className="avatar-choice-media" src={mediaImageUrl(avatar.media)} alt={mediaAltText(avatar.media, avatar.title)} />
              ) : (
                <span className="avatar-choice-fallback">{avatar.title.slice(0, 1)}</span>
              )}
              <strong>{avatar.title}</strong>
            </label>
          ))}
        </fieldset>
        <button className="button button-primary full" disabled={form.status === 'submitting'} type="submit">
          {form.status === 'submitting' ? 'Saving...' : 'Choose Learning Path'}
        </button>
        {form.status === 'success' ? <p className="form-success">Profile saved. Continue to choose a learning path.</p> : null}
        {form.status === 'fallback' ? (
          <p className="form-note">Please sign in with a parent account before saving this profile.</p>
        ) : null}
        <p className="form-note">
          Save this child profile first, then we will take you to learning path selection automatically.
        </p>
      </form>
    </section>
  );
}

type PathSelectionViewProps = {
  paths: KidsLearningPath[];
  selectedPathId: string;
  setSelectedPathId: (pathId: string) => void;
  handleContinue: () => void;
  status: 'idle' | 'submitting' | 'fallback';
  mediaImageUrl: (media: KidsMediaRelation | undefined) => string | undefined;
  mediaAltText: (media: KidsMediaRelation | undefined, fallback: string) => string;
  SectionHeader: (props: { eyebrow: string; title: string; text?: string }) => JSX.Element;
};

export function PathSelectionPageView({
  paths,
  selectedPathId,
  setSelectedPathId,
  handleContinue,
  status,
  mediaImageUrl,
  mediaAltText,
  SectionHeader,
}: PathSelectionViewProps) {
  return (
    <section className="page-frame">
      <SectionHeader
        eyebrow="Learning Path"
        title="Choose a path for the first Quran adventure."
        text="Parents can change the path later from settings or the child profile."
      />
      <div className="path-grid">
        {paths.map((path) => (
          <button
            className={`path-card ${selectedPathId === path.id ? 'selected' : ''}`}
            key={path.id}
            onClick={() => setSelectedPathId(path.id)}
            type="button"
          >
            {mediaImageUrl(path.media) ? (
              <img className="path-card-media" src={mediaImageUrl(path.media)} alt={mediaAltText(path.media, path.title)} />
            ) : null}
            <span>{path.sort_order === 1 ? 'Recommended' : 'Learning path'}</span>
            <strong>{path.title}</strong>
            <p>{path.description}</p>
          </button>
        ))}
      </div>
      <button className="button button-primary" disabled={!selectedPathId || status === 'submitting'} onClick={handleContinue} type="button">
        {status === 'submitting' ? 'Saving path...' : 'Continue to Dashboard'}
      </button>
      {status === 'fallback' ? (
        <p className="form-note">We could not save the learning path right now. Please try again before opening the child dashboard.</p>
      ) : null}
    </section>
  );
}
